/**
 * The single context holding session and scope (ADR-14).
 *
 * There is exactly one, it is populated from the session query, and it is
 * exposed READ-ONLY. No setter is published and no code path mutates the
 * payload, because it describes a decision the server made -- the client's job
 * is to render it, never to compute or amend it.
 *
 * No second context duplicates this data, and no Redux/Zustand/MobX store
 * exists anywhere in Atlas.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { AccessState, RoleResponse, SessionResponse, UserResponse } from '@/api/client';
import { useSessionQuery } from '@/api/queries';

export interface SessionScope {
  /** The server's answer, or null while it is still being fetched. */
  readonly session: SessionResponse | null;
  readonly isLoading: boolean;
  readonly error: Error | null;
  /**
   * Which of the four situations the caller is in, as decided by the server.
   *
   * Every branch in the shell reads this. Nothing branches on `role.code`:
   * working out "may this person use Atlas" from a role code would be an
   * authorization decision taken in a browser, and it could not express
   * `deactivated` at all -- that is a state of the account, not a role.
   *
   * Defaults to `anonymous` while the query is still in flight, which is the
   * safe answer to render for someone we cannot yet identify.
   */
  readonly accessState: AccessState;
  /** The caller's Atlas account, or null when anonymous. */
  readonly user: UserResponse | null;
  /**
   * The caller's role, or null when anonymous.
   *
   * `name` is for display. The client has no map from code to label, so a
   * role added by an INSERT in a later phase renders correctly with no
   * frontend change at all.
   */
  readonly role: RoleResponse | null;
  /**
   * Whether the caller may be SHOWN a navigation entry.
   *
   * Presentation only. Every endpoint authorizes server-side regardless, so a
   * hidden entry is never the security boundary -- and a visible one is never
   * a grant.
   */
  readonly canSee: (navigationKey: string) => boolean;
  /**
   * Whether the caller may be SHOWN a control, e.g. `hierarchy:manage`.
   *
   * `canSee` answers "which screens"; this answers "which controls on them" --
   * and from f4.1 the two genuinely differ, because hierarchy reads are open
   * to anyone with a role while hierarchy writes are not.
   *
   * The keys come from the server, which derives them from the same role
   * tuple its write routes enforce. Nothing here is computed from `role.code`:
   * that would be an authorization decision taken in a browser, and a source
   * assertion fails the build for it.
   *
   * Presentation only, exactly like `canSee`. A hidden button is never the
   * security boundary and a visible one is never a grant -- every endpoint
   * refuses on its own authority and records the refusal.
   */
  readonly can: (permission: string) => boolean;
}

const SessionScopeContext = createContext<SessionScope | null>(null);

export function SessionScopeProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, error } = useSessionQuery();

  const value = useMemo<SessionScope>(() => {
    const permitted = new Set(data?.navigation ?? []);
    const granted = new Set(data?.permissions ?? []);
    return {
      session: data ?? null,
      isLoading,
      error: (error as Error | null) ?? null,
      accessState: data?.access_state ?? 'anonymous',
      user: data?.user ?? null,
      role: data?.role ?? null,
      canSee: (navigationKey: string) => permitted.has(navigationKey),
      can: (permission: string) => granted.has(permission),
    };
  }, [data, isLoading, error]);

  return (
    <SessionScopeContext.Provider value={value}>{children}</SessionScopeContext.Provider>
  );
}

export function useSessionScope(): SessionScope {
  const context = useContext(SessionScopeContext);
  if (context === null) {
    throw new Error('useSessionScope must be used inside a SessionScopeProvider.');
  }
  return context;
}
